<template>
  <fin-header-footer-container :vertical-centered="$vuetify.breakpoint.mdAndUp"
                               class="pa-0"
                               content-color="#f5f8f9"
                               footer-color="#fff"
                               header-color="#fff"
                               :content-class="`${$vuetify.breakpoint.mdAndDown? 'px-0': ''}`"
                               page-color="#f5f8f9" ref="header">
    <template #header>
      <v-row class="header-row">
        <v-col v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
               v-style:paddingRight="$valueByBreakpoint($finPadding.right)"
               cols="12">
          <h1 id="header" :class="!$vuetify.breakpoint.mdAndDown ? 'py-8' : 'py-2'">
            Verteilung deines Invests festlegen
          </h1>
        </v-col>
      </v-row>
    </template>

    <template #default>
      <v-row class="px-2" justify="center" no-gutters>
        <v-col v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
               v-style:paddingRight="$valueByBreakpoint($finPadding.right)"
               cols="12"
               style="text-align:left;">
          <p class="subline mt-3 mb-6">
            <fin-lexicon-text auto-solve
                              :text="`Bestimme nun die Anzahl deiner ${assetClass.name} für dein Portfolio.`">
            </fin-lexicon-text>
          </p>
        </v-col>
      </v-row>

      <v-row class="px-2"
             no-gutters>
        <v-col v-for="(invElem, index) in investmentItems"
               :key="'portfDistItem' + index"
               v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
               v-style:paddingRight="$valueByBreakpoint($finPadding.right)"
               cols="12"
               jusitfy="center">
          <fin-portfolio-distribution-slider v-if="$vuetify.breakpoint.mdAndUp"
                                             v-model="invElem.count"
                                             :item="invElem.instrument"
                                             :maxCount="maxValue(invElem)"
                                             @input="valuesChanged"
                                             @remove-item="removeItem(invElem)"/>
          <fin-mobile-distribution-slider v-else
                                          v-model="invElem.count"
                                          :item="invElem.instrument"
                                          :maxCount="maxValue(invElem)"
                                          @input="valuesChanged"
                                          @remove-item="removeItem(invElem)"/>

          <v-divider class="my-5" v-if="investmentItems.length>1"/>
        </v-col>
      </v-row>
    </template>

    <template #footer>
      <v-container fluid>
        <v-row v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
               v-style:paddingRight="$valueByBreakpoint($finPadding.right)"
               :class="$vuetify.breakpoint.xs ? 'pt-0' : 'py-5'"
               :style="$vuetify.breakpoint.xs && !$route.fullPath.includes('finStrategist') ? 'padding-bottom: ' + mobNavHeight + 'px' : ''"
               dense>
          <v-col :align="$vuetify.breakpoint.xs ? 'center' : 'left'" cols="12" sm="4">
            <p class="remaining-capital-text mb-0">
              <span
                :style="`color: ${remainingCapital < 0 ? $finColors.accentRed: $finColors.accentGreen}; ${$vuetify.breakpoint.mdAndDown ? 'font-size: 24px !important;':''}`"
                class="remaining-capital">
                {{ remainingCapital | finCurrency }}
              </span>
              verfügbar
              <v-icon v-if="remainingCapital < 0">icon-hint</v-icon>
            </p>
            <p class="capital-invested mb-0">
              {{ bucketTotal | finCurrency }}
              von
              {{ capital | finCurrency }} investiert
            </p>
          </v-col>
          <v-col :align="$vuetify.breakpoint.xs ? 'center' : 'right'" class="ml-auto" cols="12" sm="5"
                 :style="`min-width: ${$vuetify.breakpoint.smAndUp ? '452px' : '0'}`">
            <v-btn class="mr-6" text @click="equalDistribution(investmentItems)">
              Gleichmäßig verteilen
              <v-icon class="ml-2" size="16">icon-refresh</v-icon>
            </v-btn>
            <hr class="mt-2 mb-4" v-if="$vuetify.breakpoint.xs"/>
            <v-btn class="mr-6" outlined tile @click="backButtonEvent">
              Zurück
            </v-btn>
            <v-btn :dark="remainingCapital > 0 && !instrumentCountZero"
                   :disabled="remainingCapital < 0 || instrumentCountZero"
                   raised tile @click="saveButtonEvent">
              Übernehmen
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </fin-header-footer-container>
</template>

<script>
import FinHeaderFooterContainer from '@/components/common/HeaderFooterContainer'
import FinPortfolioDistributionSlider from '@/components/common/portfolio/DistributionSlider/index'
import FinMobileDistributionSlider from "@/components/common/portfolio/DistributionSlider/mobileDistributionSlider"
import FinLexiconText from '@/components/common/lexicon/LexiconText/index'

export default {
  name: 'finPortfolioDistribution',
  components: {
    FinMobileDistributionSlider,
    FinPortfolioDistributionSlider,
    FinHeaderFooterContainer,
    FinLexiconText
  },
  props: {
    value: {
      type: Array
    },
    /** the capital to invest */
    capital: {
      type: Number,
      default: 10000
    },
    assetClass: {
      type: Object
    }
  },
  data() {
    return {
      initialItemCapital: 0,
      domRepaintKey: 1,
      hasChanged: true,
      bucketTotal: 0,
      mobNavHeight: document.querySelector('.v-bottom-navigation--fixed')?.getBoundingClientRect().height | 0
    }
  },
  methods: {
    scrollToTop() {
      this.$nextTick(() => {
        this.$refs.header.$el.scrollIntoView({
          behavior: 'smooth'
        })
      })
    },
    valuesChanged() {
      this.bucketTotal = this.investmentItems
        .filter(item => item.instrument.latestPrice.price)
        .map(item => item.count * item.instrument.latestPrice.price)
        .reduce((sum, val) => sum + val, 0)
    },
    maxValue(item) {
      return Math.floor(this.capital / item.instrument.latestPrice.price)
    },
    equalDistribution(investmentItems, capital = this.capital, keepCount = false) {
      if (!keepCount) {
        investmentItems = investmentItems.filter(item => item.instrument.latestPrice.price)
        investmentItems.forEach(item => item.count = 0)
      }

      let capitalPerItem = Math.floor(capital / investmentItems.length)
      let idxMax
      let curMax = -1

      for (let i = 0; i < investmentItems.length; i++) {
        let curPrice = investmentItems[i].instrument.latestPrice.price
        if (curPrice > capitalPerItem) {
          if (curPrice > curMax) {
            curMax = curPrice
            idxMax = i
          }
        }
      }
      // Quit if remaining capital isn't enough to buy any instrument
      investmentItems = investmentItems.filter((item, index) => index !== idxMax)
      if (investmentItems.length === 0) {
        return
      }

      capitalPerItem = Math.floor(capital / investmentItems.length)
      investmentItems.forEach(item => {
        item.count += Math.floor(capitalPerItem / item.instrument.latestPrice.price)
      })

      this.valuesChanged()
      this.equalDistribution(investmentItems, this.remainingCapital, true)
    },

    removeItem(item) {
      const investTemp = this.investmentItems.filter(me => me.instrument.id !== item.instrument.id)
      if (investTemp.length === 0) {
        this.backButtonEvent()
      }
      this.investmentItems = investTemp
      this.valuesChanged()

    },
    backButtonEvent() {
      /**
       * Event triggered when clicking the back button.
       *
       * @event switch-to-selection
       */
      this.$emit('switch-to-selection')
    },
    saveButtonEvent() {
      this.$confirm({
        title: 'Wertpapierauswahl festlegen?',
        text: 'Die von dir ausgewählten Wertpapiere werden jetzt in den Topf der Assetklasse ' + this.assetClass.name + ' übernommen. Bearbeite im Anschluss noch die fehlenden Assetklassen.',
        textWithLexikonLink: false,
        confirmBtn: 'Ok, übernehmen'
      })
        .then(() => {
          /**
           * Event triggered when clicking the save button.
           *
           * @event save-bucket-distribution
           * @type {number}
           */
          this.$emit('save-bucket-distribution', this.bucketTotal)
        })
    }
  },
  computed: {
    instrumentCountZero() {
      return this.investmentItems.map(items => items.count).reduce((prev, next) => prev + next) === 0
    },
    investmentItems: {
      get() {
        return this.value
      },
      set(val) {
        console.log('investmentItems computed', val)
        this.$emit('input', val)
      }
    },
    remainingCapital() {
      return this.capital - this.bucketTotal
    },
    sortedItems() {
      const result = this.investmentItems.slice(0)
      result.sort((a, b) => {
        if (a.instrument.rank === b.instrument.rank) return 0
        if (a.instrument.rank > b.instrument.rank) return -1
        return 1
      })
      return result
    }

  },
  mounted() {
    this.investmentItems.forEach(item => {
      if (!item.count) {
        item.count = 0
      }
    })
    this.scrollToTop()
    this.valuesChanged()
    // TODO Gleichverteilung bei item.count = 0, bzw aktien
    /* this.investmentItems.forEach(item => {
        if (!item.count) {
            this.equalDistribution()
            return
        }
    }) */
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../styles/variables.scss";

.header-row {
  background-color: #fff;
  width: 100%;
  margin: 0;
}

h1 {
  font-size: 28px;
  text-align: center;
  color: $fin-color-primary-dark;
}

.subline {
  font-family: $body-font-family;
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.38;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

.remaining-capital {
  font-family: $body-font-family;
  font-size: 32px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.19;
  letter-spacing: normal;
  text-align: right;
  color: $fin-color-accent-green;
  /* additional styling */
  margin-right: 8px;
}

.remaining-capital-text {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

.capital-invested {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

.container {
  padding-bottom: 10px !important;
}

</style>
